import { Box } from "flicket-ui";
import { ReactNode, useState } from "react";
import {
  useIntersectionObserver,
  UseIntersectionObserverOptions,
} from "~hooks";

export type IStickySection = {
  children: ReactNode | ((props: { isSticky: boolean }) => ReactNode);
  options?: UseIntersectionObserverOptions;
  offset?: number;
};

export default function StickySection({
  children,
  offset = 0,
}: IStickySection) {
  const { ref, inView } = useInViewWithPageConstraint({
    offset,
    pageConstrained: "top",
  });

  const isSticky = !inView;

  if (!children) return null;

  return (
    <>
      <Box ref={ref}>
        {typeof children === "function"
          ? children({ isSticky: false })
          : children}
      </Box>

      {/* If we are in a "sticky" condition, add a new fixed positioned element to the DOM.
      This leaves the existing element in place so the same space is taken up in the document. */}
      {isSticky && (
        <Box
          zIndex="sticky"
          css={`
            position: fixed;
            left: 0;
            right: 0;
            top: ${offset}px;
          `}
        >
          {typeof children === "function"
            ? children({ isSticky: true })
            : children}
        </Box>
      )}
    </>
  );
}

interface InViewProps extends UseIntersectionObserverOptions {
  offset?: number;
  pageConstrained?: "top" | "bottom" | undefined;
}

function useInViewWithPageConstraint({
  offset = 0,
  pageConstrained,
}: InViewProps) {
  const [inView, setInView] = useState(true);

  const ref = useIntersectionObserver({
    threshold: 1,
    root: document,
    rootMargin: `-${offset}px 0px 0px 0px`,
    onChange(isIntersecting, entry) {
      const offsetTop = entry.target?.getBoundingClientRect().top - offset ?? 0;
      if (!isIntersecting) {
        if (offsetTop < 0) {
          if (!pageConstrained || pageConstrained === "top") {
            setInView(false);
          }
        } else {
          if (!pageConstrained || pageConstrained === "bottom") {
            setInView(false);
          }
        }
      } else {
        setInView(true);
      }
    },
  });

  return { ref, inView };
}
